// client/src/hooks/useItensVenda.js - VERSÃO 100% COMPLETA E CORRIGIDA
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const ITENS_VENDA_QUERY_KEY = 'itens-venda';
const ITEM_VENDA_DETAIL_QUERY_KEY = 'item-venda';

// ✅ FUNÇÃO CRÍTICA: Validar ObjectId de forma robusta
const validateObjectId = (id, fieldName = 'ID') => {
  if (!id) {
    console.error(`❌ [VALIDATE_OBJECT_ID] ${fieldName} é undefined ou null`);
    return false;
  }
  
  // Aceitar tanto string quanto ObjectId do MongoDB
  const idString = id.toString ? id.toString() : String(id);
  
  if (typeof idString !== 'string') {
    console.error(`❌ [VALIDATE_OBJECT_ID] ${fieldName} não é string:`, typeof id, id);
    return false;
  }
  
  if (!/^[0-9a-fA-F]{24}$/.test(idString)) {
    console.error(`❌ [VALIDATE_OBJECT_ID] ${fieldName} inválido:`, idString);
    return false;
  }
  
  return true;
};

// ✅ FUNÇÃO: Mapear filtros do frontend para a API - CORRIGIDA PARA UNIDADES
const mapFilterToApi = (frontendKey) => {
  const mapping = {
    // Filtros de busca
    'search': 'search',
    'nome': 'nome',
    'sku': 'sku',
    'tags': 'tags',
    
    // ✅ CORREÇÃO: Usar nomes corretos baseados no model
    'ativo': 'ativo',
    'visivelParaCliente': 'visivelParaCliente',
    'controlaEstoque': 'controlaEstoque',
    'temEstoque': 'temEstoque',
    
    // Filtros de categoria e tipo
    'categoria': 'categoria',
    'tipoItem': 'tipoItem',
    
    // ✅✅✅ CORREÇÃO CRÍTICA: Mapear filtro de unidade para o formato CORRETO da API
    'unidade': 'unidadesDetalhes.unidade', // ✅ FILTRO POR VÍNCULO
    'unidadeDisponivel': 'unidadesDetalhes.disponivel', // ✅ FILTRO POR DISPONIBILIDADE
    'unidadeAtiva': 'unidadesDetalhes.ativo', // ✅ FILTRO POR UNIDADE ATIVA
    
    // Paginação
    'page': 'page',
    'limit': 'limit',
    
    // Ordenação
    'sort': 'sort',
    'order': 'order',
    
    // Filtros específicos da API
    'franquia': 'franquia',
    'account': 'account'
  };
  
  return mapping[frontendKey] || frontendKey;
};

// ✅✅✅ FUNÇÃO CRÍTICA: Sanitizar parâmetros para API - CORRIGIDA PARA UNIDADES
const sanitizeParams = (params = {}) => {
  const sanitized = {};
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    
    // Remover valores undefined, null ou string vazia
    if (value !== undefined && value !== null && value !== '') {
      const apiKey = mapFilterToApi(key);
      
      // ✅✅✅ CORREÇÃO CRÍTICA: Para filtros de unidade, garantir que sejam ObjectIds válidos
      // E adicionar automaticamente filtros de disponibilidade e ativação
      if ((key === 'unidade' || apiKey === 'unidadesDetalhes.unidade') && validateObjectId(value)) {
        console.log('🏢 [SANITIZE_PARAMS] Aplicando filtro de unidade com validação:', value);
        
        // ✅ CORREÇÃO: Aplicar o filtro principal de unidade
        sanitized[apiKey] = value;
        
        // ✅✅✅ CORREÇÃO CRÍTICA: Adicionar automaticamente filtros de disponibilidade e ativação
        // Isso garante que só retorne itens que estão disponíveis e ativos na unidade selecionada
        sanitized['unidadesDetalhes.disponivel'] = 'true';
        sanitized['unidadesDetalhes.ativo'] = 'true';
        
        console.log('🔍 [SANITIZE_PARAMS] Filtros de unidade aplicados:', {
          unidade: value,
          disponivel: true,
          ativo: true
        });
        
      } else if ((key === 'unidade' || apiKey === 'unidadesDetalhes.unidade') && !validateObjectId(value)) {
        console.warn(`⚠️ [SANITIZE_PARAMS] ${key} com ID inválido:`, value);
        return; // Pular filtros com IDs inválidos
      }
      // ✅ CORREÇÃO: Converter valores booleanos corretamente para a API
      else if (value === 'true' || value === 'false') {
        // Para a API, manter como string 'true'/'false'
        sanitized[apiKey] = value;
      } else if (typeof value === 'boolean') {
        sanitized[apiKey] = value.toString();
      } else if (Array.isArray(value)) {
        // Para arrays, manter apenas valores válidos
        const filteredArray = value.filter(item => item !== undefined && item !== null && item !== '');
        if (filteredArray.length > 0) {
          sanitized[apiKey] = filteredArray;
        }
      } else {
        sanitized[apiKey] = value;
      }
    }
  });
  
  console.log('🔄 [SANITIZE_PARAMS] Parâmetros sanitizados:', sanitized);
  return sanitized;
};

// ✅ FUNÇÃO: Processar resposta da API de forma segura
const processApiResponse = (responseData) => {
  if (!responseData) {
    return { 
      success: false, 
      data: [], 
      count: 0, 
      totalCount: 0, 
      pagination: {},
      error: 'Resposta da API vazia' 
    };
  }

  // Caso 1: Resposta padrão da API (success + data)
  if (responseData.success !== undefined && responseData.data !== undefined) {
    const dataArray = Array.isArray(responseData.data) ? responseData.data : [responseData.data];
    
    return {
      success: responseData.success,
      data: dataArray,
      count: responseData.count || dataArray.length,
      totalCount: responseData.totalCount || responseData.count || dataArray.length,
      pagination: responseData.pagination || {},
      message: responseData.message
    };
  }

  // Caso 2: Array direto
  if (Array.isArray(responseData)) {
    return {
      success: true,
      data: responseData,
      count: responseData.length,
      totalCount: responseData.length,
      pagination: {}
    };
  }

  // Caso 3: Outros formatos
  const data = responseData.data || responseData;
  const dataArray = Array.isArray(data) ? data : [data].filter(Boolean);
  
  return {
    success: true,
    data: dataArray,
    count: dataArray.length,
    totalCount: dataArray.length,
    pagination: responseData.pagination || {}
  };
};

// ✅ FUNÇÃO: Validar dados do item antes do envio
const validateItemData = (itemData, isUpdate = false) => {
  const errors = [];

  // Validar nome (apenas para criação)
  if (!isUpdate && (!itemData.nome || !itemData.nome.trim())) {
    errors.push('Nome do produto é obrigatório');
  }

  // Validar categoria (apenas para criação)
  if (!isUpdate) {
    if (!itemData.categoria) {
      errors.push('Categoria é obrigatória');
    } else if (!validateObjectId(itemData.categoria, 'Categoria')) {
      errors.push('Categoria selecionada é inválida');
    }
  }

  // Validar unidadesDetalhes
  if (!itemData.unidadesDetalhes || !Array.isArray(itemData.unidadesDetalhes)) {
    errors.push('Configuração de unidades é obrigatória');
  } else if (itemData.unidadesDetalhes.length === 0) {
    errors.push('Pelo menos uma unidade deve ser configurada');
  } else {
    // Validar cada unidade
    itemData.unidadesDetalhes.forEach((ud, index) => {
      if (!ud.unidade) {
        errors.push(`Unidade ${index + 1}: selecione uma unidade válida`);
      } else if (!validateObjectId(ud.unidade, `Unidade ${index + 1}`)) {
        errors.push(`Unidade ${index + 1}: ID da unidade é inválido`);
      }
      
      if (ud.preco === undefined || ud.preco === null || isNaN(ud.preco) || ud.preco < 0) {
        errors.push(`Unidade ${index + 1}: preço deve ser um número positivo`);
      }
      
      if (ud.quantidadeEmEstoque !== undefined && ud.quantidadeEmEstoque !== null) {
        const qtd = parseInt(ud.quantidadeEmEstoque);
        if (isNaN(qtd) || qtd < 0) {
          errors.push(`Unidade ${index + 1}: estoque deve ser um número não negativo`);
        }
      }
    });
  }

  return errors;
};

// ✅ FUNÇÃO: Preparar dados para envio à API - VERSÃO CORRIGIDA
const prepareItemDataForApi = (itemData) => {
  const prepared = { ...itemData };
  
  // Garantir que campos string estejam trimados
  if (prepared.nome) prepared.nome = prepared.nome.trim();
  if (prepared.descricao) prepared.descricao = prepared.descricao.trim();
  if (prepared.sku) prepared.sku = prepared.sku.trim();
  
  // Garantir que tags seja um array válido
  if (prepared.tags && Array.isArray(prepared.tags)) {
    prepared.tags = prepared.tags.map(tag => tag.trim()).filter(tag => tag);
  } else {
    prepared.tags = [];
  }
  
  // Garantir tipos numéricos nas unidades
  if (prepared.unidadesDetalhes && Array.isArray(prepared.unidadesDetalhes)) {
    prepared.unidadesDetalhes = prepared.unidadesDetalhes.map(ud => ({
      unidade: ud.unidade,
      preco: parseFloat(ud.preco) || 0,
      quantidadeEmEstoque: parseInt(ud.quantidadeEmEstoque) || 0,
      disponivel: ud.disponivel !== false,
      ativo: ud.ativo !== false
    }));
  }
  
  // ✅ CORREÇÃO CRÍTICA: Limpar metadata.fornecedor se for string vazia
  if (prepared.metadata) {
    const metadata = { ...prepared.metadata };
    
    // ✅ REMOVER fornecedor se for string vazia ou inválida
    if (metadata.fornecedor === '' || !validateObjectId(metadata.fornecedor)) {
      delete metadata.fornecedor;
    }
    
    // ✅ Garantir que campos numéricos sejam números
    prepared.metadata = {
      tipoItem: metadata.tipoItem || 'fisico',
      peso: parseFloat(metadata.peso) || 0,
      dimensoes: {
        comprimento: parseFloat(metadata.dimensoes?.comprimento) || 0,
        largura: parseFloat(metadata.dimensoes?.largura) || 0,
        altura: parseFloat(metadata.dimensoes?.altura) || 0
      },
      marca: metadata.marca?.trim() || '',
      modelo: metadata.modelo?.trim() || '',
      codigoBarras: metadata.codigoBarras?.trim() || '',
      // ✅ Fornecedor só é incluído se for válido
      ...(metadata.fornecedor && { fornecedor: metadata.fornecedor }),
      custo: parseFloat(metadata.custo) || 0,
      margemLucro: parseFloat(metadata.margemLucro) || 0
    };
  }
  
  return prepared;
};

// ======================================================
// 🎯 HOOKS DE CONSULTA - 100% CORRIGIDOS
// ======================================================

export const useItensVenda = (params = {}) => {
  return useQuery({
    queryKey: [ITENS_VENDA_QUERY_KEY, params],
    queryFn: async () => {
      console.log('🔍 [USE_ITENS_VENDA] Buscando itens com params:', params);

      const cleanParams = sanitizeParams(params);

      try {
        const response = await api.get('/itens-venda', { 
          params: cleanParams,
          timeout: 30000
        });
        
        console.log('✅ [USE_ITENS_VENDA] Resposta da API recebida:', {
          dataCount: response.data.data?.length || 0,
          totalCount: response.data.totalCount,
          success: response.data.success,
          // ✅ CORREÇÃO: Log detalhado para debug de filtros
          filtroUnidade: cleanParams['unidadesDetalhes.unidade'],
          filtrosAplicados: cleanParams
        });
        
        return processApiResponse(response.data);
        
      } catch (error) {
        console.error('❌ [USE_ITENS_VENDA] Erro na requisição:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          // ✅ CORREÇÃO: Log dos parâmetros que causaram o erro
          paramsEnviados: cleanParams
        });
        
        // Retornar estrutura padrão mesmo em caso de erro
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {
            page: parseInt(cleanParams.page) || 1,
            limit: parseInt(cleanParams.limit) || 10,
            totalPages: 0,
            totalCount: 0,
            hasNext: false,
            hasPrev: false
          },
          error: error.response?.data?.error || error.response?.data?.message || error.message
        };
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10,   // 10 minutos
    retry: (failureCount, error) => {
      // Não tentar novamente para erros 4xx (exceto 408 timeout)
      if (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 408) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useItemVenda = (itemId, options = {}) => {
  return useQuery({
    queryKey: [ITEM_VENDA_DETAIL_QUERY_KEY, itemId],
    queryFn: async () => {
      if (!itemId) {
        console.error('❌ [USE_ITEM_VENDA] Item ID não fornecido');
        return null;
      }

      if (!validateObjectId(itemId, 'Item ID')) {
        console.error('❌ [USE_ITEM_VENDA] Item ID inválido:', itemId);
        return null;
      }
      
      console.log('🔍 [USE_ITEM_VENDA] Buscando item específico:', itemId);
      
      try {
        const response = await api.get(`/itens-venda/${itemId}`, { 
          timeout: 15000
        });
        
        console.log('✅ [USE_ITEM_VENDA] Item encontrado');
        return response.data?.data || response.data || null;
        
      } catch (error) {
        console.error('❌ [USE_ITEM_VENDA] Erro ao buscar item:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        
        // Em caso de 404, retornar null (item não existe)
        if (error.response?.status === 404) {
          return null;
        }
        
        throw error;
      }
    },
    enabled: !!itemId && validateObjectId(itemId) && (options.enabled !== false),
    staleTime: 1000 * 60 * 3, // 3 minutos para dados específicos
    retry: 1,
  });
};

// ======================================================
// 🎯 HOOK DE MUTAÇÕES - 100% CORRIGIDO E OTIMIZADO
// ======================================================

export const useMutateItensVenda = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  // ✅ FUNÇÃO CRÍTICA: Gerenciamento de cache
  const invalidateCaches = (itemId = null) => {
    try {
      console.log('🔄 [CACHE] Invalidando caches...', { itemId });
      
      // Invalidar queries principais
      queryClient.invalidateQueries({ 
        queryKey: [ITENS_VENDA_QUERY_KEY] 
      });
      
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ 
        queryKey: ['categorias'] 
      });
      
      queryClient.invalidateQueries({ 
        queryKey: ['unidades'] 
      });

      // Invalidar item específico se fornecido
      if (itemId && validateObjectId(itemId)) {
        queryClient.invalidateQueries({ 
          queryKey: [ITEM_VENDA_DETAIL_QUERY_KEY, itemId] 
        });
      }

      console.log('✅ [CACHE] Caches invalidados com sucesso');
    } catch (error) {
      console.error('❌ [CACHE] Erro ao invalidar cache:', error);
    }
  };

  // ✅ FUNÇÃO: Tratamento de erro padronizado
  const handleError = (error, defaultMessage = 'Erro na operação') => {
    console.error('❌ [MUTATION] Erro detalhado:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    const errorMessage = error.response?.data?.message || 
                       error.response?.data?.error || 
                       error.message || 
                       defaultMessage;
    
    setAlert(errorMessage, 'danger');
    return errorMessage;
  };

  // ✅ FUNÇÃO: Tratamento de sucesso padronizado
  const handleSuccess = (message, itemId = null) => {
    console.log('✅ [MUTATION] Sucesso:', message);
    invalidateCaches(itemId);
    setAlert(message, 'success');
  };

  // ✅ MUTATION: Criar item
  const addItemVenda = useMutation({
    mutationFn: async (itemData) => {
      console.log('🆕 [MUTATION] Criando novo item...');
      
      const validationErrors = validateItemData(itemData, false);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join('; '));
      }
      
      const preparedData = prepareItemDataForApi(itemData);
      console.log('📤 [MUTATION] Dados preparados para criação:', preparedData);
      
      const response = await api.post('/itens-venda', preparedData, {
        timeout: 30000
      });
      
      return response.data;
    },
    onSuccess: (response) => {
      const itemId = response.data?._id || response.data?.id;
      handleSuccess('Produto criado com sucesso!', itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao criar produto');
    },
  });

  // ✅ MUTATION: Atualizar item
  const updateItemVenda = useMutation({
    mutationFn: async ({ id, itemData }) => {
      console.log('✏️ [MUTATION] Atualizando item:', id);
      
      if (!validateObjectId(id, 'Item ID para atualização')) {
        throw new Error('ID do item é inválido');
      }
      
      const validationErrors = validateItemData(itemData, true);
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join('; '));
      }
      
      const preparedData = prepareItemDataForApi(itemData);
      console.log('📤 [MUTATION] Dados preparados para atualização:', preparedData);
      
      const response = await api.put(`/itens-venda/${id}`, preparedData, {
        timeout: 30000
      });
      
      return response.data;
    },
    onMutate: async (variables) => {
      // Cancelar queries para evitar conflitos
      await queryClient.cancelQueries({ queryKey: [ITENS_VENDA_QUERY_KEY] });
      await queryClient.cancelQueries({ queryKey: [ITEM_VENDA_DETAIL_QUERY_KEY, variables.id] });
      
      console.log('⚡ [MUTATION] Atualização otimista iniciada');
    },
    onSuccess: (response, variables) => {
      handleSuccess('Produto atualizado com sucesso!', variables.id);
    },
    onError: (error, variables) => {
      handleError(error, 'Erro ao atualizar produto');
    },
    onSettled: (data, error, variables) => {
      invalidateCaches(variables.id);
    }
  });

  // ✅ MUTATION: Deletar item
  const deleteItemVenda = useMutation({
    mutationFn: async (itemId) => {
      console.log('🗑️ [MUTATION] Excluindo item:', itemId);
      
      if (!validateObjectId(itemId, 'Item ID para exclusão')) {
        throw new Error('ID do item é inválido');
      }
      
      const response = await api.delete(`/itens-venda/${itemId}`, {
        timeout: 30000
      });
      
      return response.data;
    },
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: [ITENS_VENDA_QUERY_KEY] });
      
      // Otimistic UI update
      const previousItens = queryClient.getQueryData([ITENS_VENDA_QUERY_KEY]);
      
      if (previousItens?.data && Array.isArray(previousItens.data)) {
        const newData = {
          ...previousItens,
          data: previousItens.data.filter(item => item._id !== itemId),
          count: Math.max(0, previousItens.count - 1),
          totalCount: Math.max(0, previousItens.totalCount - 1)
        };
        
        queryClient.setQueryData([ITENS_VENDA_QUERY_KEY], newData);
      }
      
      return { previousItens };
    },
    onSuccess: (response, itemId) => {
      handleSuccess('Produto excluído com sucesso!', itemId);
    },
    onError: (error, itemId, context) => {
      // Reverter otimistic update em caso de erro
      if (context?.previousItens) {
        queryClient.setQueryData([ITENS_VENDA_QUERY_KEY], context.previousItens);
      }
      
      handleError(error, 'Erro ao excluir produto');
    },
    onSettled: (data, error, itemId) => {
      invalidateCaches(itemId);
    }
  });

  // ✅ MUTATION: Upload de imagem
  const uploadItemVendaImage = useMutation({
    mutationFn: async ({ itemId, file }) => {
      console.log('🖼️ [MUTATION] Upload de imagem para item:', itemId);
      
      if (!validateObjectId(itemId, 'Item ID para upload')) {
        throw new Error('ID do item é inválido');
      }
      
      if (!file || !(file instanceof File)) {
        throw new Error('Arquivo de imagem inválido');
      }
      
      if (!file.type.startsWith('image/')) {
        throw new Error('O arquivo deve ser uma imagem');
      }
      
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('A imagem não pode exceder 5MB');
      }
      
      const formData = new FormData();
      formData.append('imagem', file);
      
      const response = await api.post(`/itens-venda/${itemId}/imagens`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Imagem adicionada com sucesso!', variables.itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao fazer upload da imagem');
    },
  });

  // ✅ MUTATION: Remover imagem
  const deleteItemVendaImage = useMutation({
    mutationFn: async ({ itemId, imagemId }) => {
      console.log('🖼️ [MUTATION] Removendo imagem:', { itemId, imagemId });
      
      if (!validateObjectId(itemId, 'Item ID para remoção de imagem')) {
        throw new Error('ID do item é inválido');
      }
      
      if (!validateObjectId(imagemId, 'Imagem ID')) {
        throw new Error('ID da imagem é inválido');
      }
      
      const response = await api.delete(`/itens-venda/${itemId}/imagens/${imagemId}`, {
        timeout: 30000
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Imagem removida com sucesso!', variables.itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao remover imagem');
    },
  });

  // ✅ MUTATION: Definir imagem principal
  const setItemVendaImagemPrincipal = useMutation({
    mutationFn: async ({ itemId, tipo, refId }) => {
      console.log('⭐ [MUTATION] Definindo imagem principal:', { itemId, tipo, refId });
      
      if (!validateObjectId(itemId, 'Item ID')) {
        throw new Error('ID do item é inválido');
      }
      
      if (!validateObjectId(refId, 'Imagem ID')) {
        throw new Error('ID da imagem é inválido');
      }
      
      if (!['galeria', 'custom'].includes(tipo)) {
        throw new Error('Tipo deve ser "galeria" ou "custom"');
      }
      
      const response = await api.patch(`/itens-venda/${itemId}/imagem-principal`, {
        tipo,
        refId
      }, {
        timeout: 15000
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Imagem principal definida com sucesso!', variables.itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao definir imagem principal');
    },
  });

  // ✅ MUTATION: Alternar status
  const toggleStatusItemVenda = useMutation({
    mutationFn: async (itemId) => {
      console.log('🔄 [MUTATION] Alternando status do item:', itemId);
      
      if (!validateObjectId(itemId, 'Item ID')) {
        throw new Error('ID do item é inválido');
      }
      
      const response = await api.patch(`/itens-venda/${itemId}/alternar-status`, {}, {
        timeout: 15000
      });
      
      return response.data;
    },
    onSuccess: (response, itemId) => {
      handleSuccess('Status alterado com sucesso!', itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao alterar status');
    },
  });

  // ✅ MUTATION: Configurar unidade
  const configurarUnidadeItem = useMutation({
    mutationFn: async ({ itemId, unidadeId, dadosUnidade }) => {
      console.log('🏢 [MUTATION] Configurando unidade:', { itemId, unidadeId, dadosUnidade });
      
      if (!validateObjectId(itemId, 'Item ID')) {
        throw new Error('ID do item é inválido');
      }
      
      if (!validateObjectId(unidadeId, 'Unidade ID')) {
        throw new Error('ID da unidade é inválido');
      }
      
      const response = await api.patch(`/itens-venda/${itemId}/unidades/${unidadeId}`, dadosUnidade, {
        timeout: 15000
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Unidade configurada com sucesso!', variables.itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao configurar unidade');
    },
  });

  // ✅ MUTATION: Remover unidade
  const removerUnidadeItem = useMutation({
    mutationFn: async ({ itemId, unidadeId }) => {
      console.log('🗑️ [MUTATION] Removendo unidade:', { itemId, unidadeId });
      
      if (!validateObjectId(itemId, 'Item ID')) {
        throw new Error('ID do item é inválido');
      }
      
      if (!validateObjectId(unidadeId, 'Unidade ID')) {
        throw new Error('ID da unidade é inválido');
      }
      
      const response = await api.delete(`/itens-venda/${itemId}/unidades/${unidadeId}`, {
        timeout: 15000
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Unidade removida com sucesso!', variables.itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao remover unidade');
    },
  });

  // ✅ MUTATION: Atualizar estoque
  const atualizarEstoqueUnidade = useMutation({
    mutationFn: async ({ itemId, unidadeId, dadosEstoque }) => {
      console.log('📦 [MUTATION] Atualizando estoque:', { itemId, unidadeId, dadosEstoque });
      
      if (!validateObjectId(itemId, 'Item ID')) {
        throw new Error('ID do item é inválido');
      }
      
      if (!validateObjectId(unidadeId, 'Unidade ID')) {
        throw new Error('ID da unidade é inválido');
      }
      
      const response = await api.patch(`/itens-venda/${itemId}/unidades/${unidadeId}/estoque`, dadosEstoque, {
        timeout: 15000
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Estoque atualizado com sucesso!', variables.itemId);
    },
    onError: (error) => {
      handleError(error, 'Erro ao atualizar estoque');
    },
  });

  // ✅ Estados de loading consolidados
  const isLoading = {
    add: addItemVenda.isPending,
    update: updateItemVenda.isPending,
    delete: deleteItemVenda.isPending,
    uploadImage: uploadItemVendaImage.isPending,
    removeImage: deleteItemVendaImage.isPending,
    setMainImage: setItemVendaImagemPrincipal.isPending,
    toggleStatus: toggleStatusItemVenda.isPending,
    configureUnit: configurarUnidadeItem.isPending,
    removeUnit: removerUnidadeItem.isPending,
    updateStock: atualizarEstoqueUnidade.isPending,
  };

  const isPending = Object.values(isLoading).some(status => status);

  return {
    // ✅ MUTAÇÕES PRINCIPAIS
    addItemVenda,
    updateItemVenda,
    deleteItemVenda,
    
    // ✅ MUTAÇÕES DE IMAGEM
    uploadItemVendaImage,
    deleteItemVendaImage,
    setItemVendaImagemPrincipal,
    
    // ✅ MUTAÇÕES DE STATUS
    toggleStatusItemVenda,
    
    // ✅ MUTAÇÕES DE UNIDADES
    configurarUnidadeItem,
    removerUnidadeItem,
    atualizarEstoqueUnidade,
    
    // ✅ ESTADOS
    isPending,
    isLoading,
    
    // ✅ UTILITÁRIOS
    invalidateCaches,
    refetchAll: () => invalidateCaches(),
    
    // ✅ VALIDAÇÕES (exportadas para uso externo)
    validateObjectId,
    validateItemData,
    prepareItemDataForApi,
    sanitizeParams // ✅ EXPORTAR PARA USO NO COMPONENTE
  };
};

// ======================================================
// 🎯 HOOK COMPOSTO PARA GERENCIAMENTO - 100% CORRIGIDO
// ======================================================

export const useItemVendaManagement = (params = {}) => {
  const itensVendaQuery = useItensVenda(params);
  const mutations = useMutateItensVenda();

  return {
    // ✅ DADOS
    data: itensVendaQuery.data,
    itens: itensVendaQuery.data?.data || [],
    pagination: itensVendaQuery.data?.pagination || {},
    
    // ✅ ESTADOS DA CONSULTA
    isLoading: itensVendaQuery.isLoading,
    isFetching: itensVendaQuery.isFetching,
    isError: itensVendaQuery.isError,
    error: itensVendaQuery.error,
    
    // ✅ MUTAÇÕES
    ...mutations,
    
    // ✅ AÇÕES
    refetch: itensVendaQuery.refetch,
    refetchAll: () => {
      itensVendaQuery.refetch();
      mutations.invalidateCaches();
    },
    
    // ✅ ESTATÍSTICAS
    totalItens: itensVendaQuery.data?.totalCount || 0,
    currentCount: itensVendaQuery.data?.count || 0,
    
    // ✅ UTILITÁRIOS
    hasData: !itensVendaQuery.isLoading && (itensVendaQuery.data?.data?.length > 0),
    isEmpty: !itensVendaQuery.isLoading && (!itensVendaQuery.data?.data || itensVendaQuery.data.data.length === 0)
  };
};

// ======================================================
// 🎯 HOOKS ESPECIALIZADOS - 100% CORRIGIDOS
// ======================================================

export const useItensVendaSearch = (searchTerm, options = {}) => {
  const params = {
    search: searchTerm,
    limit: options.limit || 10,
    ativo: true,
    visivelParaCliente: true,
    ...options
  };

  return useItensVenda(params);
};

export const useItensVendaEstoqueBaixo = (limiteEstoque = 5, options = {}) => {
  const params = {
    temEstoque: 'true',
    controlaEstoque: 'true',
    ativo: true,
    limit: options.limit || 50,
    ...options
  };

  return useItensVenda(params);
};

export const useItensVendaPorCategoria = (categoriaId, options = {}) => {
  const params = {
    categoria: categoriaId,
    ativo: true,
    visivelParaCliente: true,
    ...options
  };

  return useItensVenda(params);
};

// ✅ HOOKS ESPECIALIZADOS PARA FILTRO POR UNIDADE
export const useItensVendaPorUnidade = (unidadeId, options = {}) => {
  const params = {
    'unidadesDetalhes.unidade': unidadeId,
    ativo: true,
    visivelParaCliente: true,
    ...options
  };

  return useItensVenda(params);
};

export const useItensVendaDisponiveisPorUnidade = (unidadeId, options = {}) => {
  const params = {
    'unidadesDetalhes.unidade': unidadeId,
    'unidadesDetalhes.disponivel': 'true',
    'unidadesDetalhes.ativo': 'true',
    ativo: true,
    visivelParaCliente: true,
    ...options
  };

  return useItensVenda(params);
};

export const useItensVendaComEstoquePorUnidade = (unidadeId, options = {}) => {
  const params = {
    'unidadesDetalhes.unidade': unidadeId,
    'unidadesDetalhes.quantidadeEmEstoque': { $gt: 0 },
    'unidadesDetalhes.disponivel': 'true',
    'unidadesDetalhes.ativo': 'true',
    ativo: true,
    controlaEstoque: true,
    ...options
  };

  return useItensVenda(params);
};

export const useItensVendaAtivos = (options = {}) => {
  const params = {
    ativo: true,
    visivelParaCliente: true,
    ...options
  };

  return useItensVenda(params);
};

// ✅ Histórico de movimentação de estoque (ledger — ver
// server/models/MovimentacaoEstoque.js) de um produto específico.
export const useMovimentacoesEstoque = (itemId, { page = 1, limit = 25, unidadeId } = {}) => {
  return useQuery({
    queryKey: ['movimentacoes-estoque', itemId, page, limit, unidadeId],
    queryFn: async () => {
      const response = await api.get(`/itens-venda/${itemId}/movimentacoes-estoque`, {
        params: { page, limit, unidadeId }
      });
      return response.data;
    },
    enabled: !!itemId,
    placeholderData: (previousData) => previousData,
  });
};

export default useItensVenda;